import { cosineSimilarity } from "./ms/cosine_similarity.js";
import { appendMass } from "./peaks/append_mass.js";
import { getPeaks } from "./peaks/get_peaks.js";
import { vectorify } from "./vectorify.js";
/**
 * Preprocessing task over the signalsj
 * @param chromatogram - Chromatogram to process
 * @param options - Options object (same as spectraComparison)
 * @returns Array of peaks, integrated mass spectra and weighted mass spectra
 */
function preprocessing(chromatogram, options) {
    // peak picking
    let peaks = getPeaks(chromatogram, options);
    peaks = peaks.sort((a, b) => a.from - b.to);
    // integrate mass in the peaks
    const integratedMs = appendMass(chromatogram, peaks);
    const vector = vectorify(integratedMs, options);
    return {
        peaks,
        integratedMs,
        vector,
    };
}
const defaultOptions = {
    thresholdFactor: 0,
    maxNumberPeaks: Number.MAX_VALUE,
    groupWidth: 0,
    heightFilter: 2,
    massPower: 3,
    intPower: 0.6,
};
/**
 * Returns the most similar peaks between two GC/MS and their similarities.
 * @returns Most similar peaks and their similarities.
 */
export function spectraComparison(chrom1, chrom2, options = {}) {
    const { similarityThreshold = 0.7 } = options;
    options = { ...defaultOptions, ...options };
    // peak picking
    const reference = preprocessing(chrom1, options);
    const sample = preprocessing(chrom2, options);
    // similarity between MS
    const len = Math.max(sample.peaks.length, reference.peaks.length);
    const similarityPeaks = {
        chrom1: new Array(len),
        chrom2: new Array(len),
        similarity: new Array(len),
    };
    let similarLen = 0;
    // Similarity matrix
    for (let i = 0; i < sample.peaks.length; ++i) {
        let max = {
            similarity: -3,
            chrom1: reference.peaks[0],
            chrom2: sample.peaks[0],
        };
        let biggerCounter = 0;
        for (let j = 0; j < reference.peaks.length; ++j) {
            const sim = cosineSimilarity(sample.vector[i].x, sample.vector[i].y, reference.vector[j].x, reference.vector[j].y);
            if (sim > similarityThreshold && sim > max.similarity) {
                max = {
                    similarity: sim,
                    chrom1: reference.peaks[j],
                    chrom2: sample.peaks[i],
                };
            }
            if (sim > similarityThreshold) {
                ++biggerCounter;
            }
        }
        if (biggerCounter === 1) {
            similarityPeaks.chrom1[similarLen] = max.chrom1;
            similarityPeaks.chrom2[similarLen] = max.chrom2;
            similarityPeaks.similarity[similarLen++] = max.similarity;
        }
    }
    similarityPeaks.chrom1.length = similarLen;
    similarityPeaks.chrom2.length = similarLen;
    const duplicates = {};
    for (let i = 0; i < similarLen; ++i) {
        if (duplicates[similarityPeaks.chrom1[i].retentionTime]) {
            duplicates[similarityPeaks.chrom1[i].retentionTime].push(i);
        }
        else {
            duplicates[similarityPeaks.chrom1[i].retentionTime] = [i];
        }
    }
    const peaksChrom1 = [];
    const peaksChrom2 = [];
    const peaksSimilarity = [];
    for (const val in duplicates) {
        if (duplicates[val].length === 1) {
            peaksChrom1.push(similarityPeaks.chrom1[duplicates[val][0]]);
            peaksChrom2.push(similarityPeaks.chrom2[duplicates[val][0]]);
            peaksSimilarity.push(similarityPeaks.similarity[duplicates[val][0]]);
        }
    }
    return {
        peaksFirst: peaksChrom1,
        peaksSecond: peaksChrom2,
        peaksSimilarity,
    };
}
//# sourceMappingURL=spectra_comparison.js.map